import { inject, Injectable } from '@angular/core';

import { Facet, FacetValue } from '@spartacus/core';
import { FacetService } from '@spartacus/storefront';

import { isNumber } from '@app/core/types/guards/is-number.type-guard';

import { FacetRangeFilter } from '../types';

@Injectable({
  providedIn: 'root'
})
export class DpFacetRangeService {
  readonly #facetService = inject(FacetService);

  computeRangeState(facetValues: FacetValue[] = []): FacetRangeFilter {
    const values = facetValues?.map((value) => Number(value?.name)).filter((value) => isNumber(value)) ?? [];
    const from = values.length ? Math.min(...values) : 0;
    const to = values.length ? Math.max(...values) : 0;
    const step = this.#getStep(values);

    return { from, to, step };
  }

  getSelectedValues(facetValues: FacetValue[] = []): FacetRangeFilter | undefined {
    const selectedValues = facetValues.filter((item) => item?.selected);
    if (selectedValues.length) {
      return this.computeRangeState(selectedValues);
    }
  }

  generateQueryParams(facet: Facet, range: Partial<FacetRangeFilter>): { [key: string]: string } | null {
    const rangedFacetValues = this.getRangedValues(facet, range)
      .map((facetValue: FacetValue) => `${facetValue?.name}`)
      .join('~');
    if (!rangedFacetValues.length) {
      return null;
    }
    const regex = new RegExp(`:${facet.facetCode}:([^:]+)`);
    const baseQuery = decodeURIComponent(facet?.query?.query?.value ?? '').replace(regex, '');
    const joinedFacetValues = `${baseQuery}:${facet.facetCode}:${rangedFacetValues ?? ''}`;
    return this.#facetService.getLinkParams(joinedFacetValues);
  }

  getRangedValues(facet: Facet, range: Partial<FacetRangeFilter>): FacetValue[] {
    return (
      facet?.values?.filter(
        (value: FacetValue) => Number(range?.from) <= Number(value.name) && Number(range?.to) >= Number(value.name)
      ) ?? []
    );
  }

  #getStep(values: number[]): number {
    const maxDecimalPlaces = Math.max(...values.map(this.#countDecimalPlaces), 0);
    return Math.pow(10, -maxDecimalPlaces);
  }

  #countDecimalPlaces(num: number): number {
    return num.toString().split('.')[1]?.length || 0;
  }
}
